import { styled, StyledProps } from '@glitz/react';
import React, { ChangeEventHandler, useState, useEffect } from 'react';
import * as style from 'Shared/Style';

export enum SearchBorderStyle {
  Surrounding,
  Underline,
}

const SearchInputElement = styled.input({
  border: { xy: { width: 0 } },
  padding: { xy: 0 },
  flexGrow: 1,
  fontFamily: 'Futura PT',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '16px',
  lineHeight: '130%',
})

export const SearchInput = styled(
  (
    props: {
      value?: string;
      placeholderText?: string;
      onUpdate: ChangeEventHandler<HTMLInputElement>;
      iconBefore?: React.ReactNode;
      iconAfter?: React.ReactNode;
      paddingX?: number;
      paddingY?: number;
      borderStyle?: SearchBorderStyle;
    } & StyledProps,
  ) => {
    const {
      value,
      placeholderText,
      onUpdate,
      iconBefore,
      iconAfter,
      paddingX = 16,
      paddingY = 12,
      borderStyle = SearchBorderStyle.Surrounding,
    } = props;
    return (
      <styled.Div
        css={{
          width: '100%',
          border:
            borderStyle === SearchBorderStyle.Surrounding
              ? { xy: { width: '2px', style: 'solid', color: style.colors.monochrome.black } }
              : borderStyle === SearchBorderStyle.Underline
              ? { bottom: { width: '1px', style: 'solid', color: style.colors.monochrome.black } }
              : {},
          borderRadius: borderStyle === SearchBorderStyle.Surrounding ? '30px' : undefined,
          position: 'relative',
          padding: {
            x: `${paddingX}px`,
            y: `${paddingY}px`,
          },
          display: 'flex',
          gap: '12px',
        }}
      >
        {iconBefore}
        <SearchInputElement
          value={value}
          placeholder={placeholderText}
          onChange={onUpdate}
        />
        {iconAfter}
      </styled.Div>
    );
  },
);

const ToggleLabel = styled.label({
  position: 'relative',
  display: 'inline-block',
  width: '28px',
  height: '16px',
  outlineStyle: 'none',
  borderRadius: '38px',
  cursor: 'pointer',
  border: {
    xy: {
      width: '1px',
      style: 'solid',
      color: style.colors.monochrome.black,
    },
  },
})

const ToggleInput = styled.input({
  position: 'absolute',
  top: '-99999px',
  left: '-99999px',
})

const ToggleSwitch = styled.div({
  position: 'absolute',
  cursor: 'pointer',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  borderRadius: '38px',
  ':before': {
    position: 'absolute',
    content: "''",
    height: '12px',
    width: '12px',
    left: '1px',
    bottom: '1px',
    borderRadius: '50%',
  },
})

export const Toggle = styled(
  ({
    initialValue = true,
    onChange,
  }: {
    initialValue?: boolean;
    onChange: (value: boolean) => void;
  } & StyledProps) => {
    const [value, setValue] = useState(initialValue);
    const [stateInitialValue, setInitialValue] = useState(initialValue);

    useEffect(() => {
      setValue(stateInitialValue);
    }, [stateInitialValue]);

    const TRANSITION_DURATION = '.4s';
    return (
      <ToggleLabel
        css={{
          transitionDuration: TRANSITION_DURATION,
          border: {
            xy: {
              color: value ? style.colors.brandColors.green : style.colors.monochrome.black,
            },
          },
        }}
      >
        <ToggleInput
          type="checkbox"
          checked={value}
          onChange={(event) => {
            setValue((currentValue) => !currentValue);
            onChange(event.currentTarget.checked);
          }}
        />
        <ToggleSwitch
          css={{
            transitionDuration: TRANSITION_DURATION,
            backgroundColor: value ? style.colors.brandColors.green : style.colors.monochrome.white,
            ':before': {
              backgroundColor: value ? style.colors.monochrome.white : style.colors.monochrome.black,
              transitionDuration: TRANSITION_DURATION,
              transform: value ? 'translateX(12px)' : undefined,
            },
          }}
        ></ToggleSwitch>
      </ToggleLabel>
    );
  },
);
